import { useCallback, useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { getTemplateConfig } from '@/constants/templateConfig';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { mixpanelService } from '@/services/mixpanelService';
import { getCheckoutTemplate } from '@/templates/TemplateRegistry';
import { getDeviceType } from '@/utils/device';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';

/**
 * "Use other payment method" — swap to the active template's
 * {@link CheckoutTemplate.fallbackTemplateType} and pre-select that
 * template's default method. PREFERRED lands on DEFAULT; the embedded
 * preferred template lands on EMBEDDED_VIEW.
 */
export const useOtherPaymentMethodFallback = () => {
  const context = useContext(CheckoutContext);
  const {
    paymentMethodsAvailable,
    boot,
    template,
    actions: { setTemplate, setPaymentMethodSelected },
  } = context;

  const handleUseOtherPaymentMethod = useCallback(() => {
    mixpanelService.track(
      MixpanelEvents.USE_OTHER_PAYMENT_METHOD_CLICKED,
      getMixpanelProperties(context) as Record<string, unknown>
    );
    const fallbackType = getCheckoutTemplate(template.type).fallbackTemplateType;
    const nextDefaultMethod = getCheckoutTemplate(fallbackType).resolveDefaultPaymentMethod({
      device: getDeviceType(),
      paymentMethods: paymentMethodsAvailable,
      bootData: boot,
    });
    if (nextDefaultMethod) {
      setPaymentMethodSelected(nextDefaultMethod);
    }
    setTemplate(getTemplateConfig(fallbackType));
  }, [boot, context, paymentMethodsAvailable, setPaymentMethodSelected, setTemplate, template.type]);

  return { handleUseOtherPaymentMethod };
};
